/**
 * The Ustawienia tab's wiring: the policy and the hours it is read beside, one gate over both, and
 * the one-field change — drawn at once, sent at once, and taken back if refused.
 *
 * What the rows say is `model/policySettings`'; what the phone shows is `useClientPreview`'s. This
 * hook reads, changes and announces.
 *
 * ── A change is optimistic, and a refusal puts it back ───────────────────────────────────────
 * „Każda zmiana zapisuje się od razu" (T-B1): a pick or a switch repaints the tab on the press and
 * sends `PATCH /booking-policy` with that one field.
 *
 * - ON THE WIRE the PATCHes share one mutation scope: they reach the server one at a time, in the
 *   order they were made, so the last pick is the server's last write.
 * - IN THE CACHE each field keeps a sequence number: an answer or a refusal for an older change of
 *   a field never overwrites a newer optimistic value of that field. The answer replaces only its
 *   own field — another field may have a change of its own still out.
 * - A REFUSAL puts back its field as it stood before the press and says so under that row; of two
 *   quick changes where the first is refused, the first goes back and the second stands.
 *
 * The callbacks live on the mutation, so they run after the tab has unmounted too. Once nothing is
 * out, the tab reads everything again (`readBookingAgain`),
 * because the step, the length and the buffer change the calendar's free starts too.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { patchBookingPolicy } from '~/shared/api/generated/endpoints/booking-policy/booking-policy';
import type { TrainWithMeModulesBookingDtosResponsesBookingPolicyResponse as BookingPolicy } from '~/shared/api/generated/model';
import { ApiError } from '~/shared/api/mutator';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import { useSkeletonGate, type SkeletonGate } from '~/shared/lib/useSkeletonGate';
import { bookingPolicyQueryOptions, readBookingAgain } from '~/features/credit-ledger';

import {
  POLICY_WRITE,
  policyWritesSettled,
  trainerAvailabilityQueryOptions,
} from '../api/bookingQueries';
import {
  DEFAULT_POLICY,
  DEFAULT_WEEK_MINUTES,
  policyFieldValue,
  policyPatch,
  policySettingsView,
  policyPick,
  policyWithChange,
  type PickValue,
  type PolicyField,
  type PolicyPick,
  type PolicyPickField,
  type SettingsContext,
  type SettingsGroup,
} from '../model/policySettings';
import { scheduleReadRange } from '../model/scheduleDeviations';
import { readState } from '../model/sessionCredit';
import { scheduleRefusal } from '../model/scheduleRefusal';
import { bookingZone, wallReading } from '../model/trainerClock';
import { weekPatternOf, weeklyMinutes } from '../model/weekPattern';
import { useClientPreview, type ClientPreviewState } from './useClientPreview';
import { useMinuteClock } from './useMinuteClock';

export type PolicySettingsContent =
  /** The wait, drawn in the default policy's shape — see `DEFAULT_POLICY`. */
  | { readonly kind: 'waiting'; readonly groups: readonly SettingsGroup[] }
  | { readonly kind: 'failed' }
  | {
      readonly kind: 'ready';
      readonly policy: BookingPolicy;
      readonly groups: readonly SettingsGroup[];
      readonly context: SettingsContext;
    };

export interface PolicySettingsState {
  readonly gate: SkeletonGate;
  readonly content: PolicySettingsContent;
  readonly pick: (field: PolicyPickField) => PolicyPick | null;
  readonly change: (field: PolicyField, value: PickValue | boolean) => void;
  /** Each row's last refused change, said under that row. */
  readonly refusals: Readonly<Partial<Record<PolicyField, string>>>;
  readonly announcement: string;
  readonly retry: () => void;
  readonly nowMs: number;
  readonly zone: string;
  /** The phone: what a client would see, from the policy on screen. */
  readonly preview: ClientPreviewState;
}

interface PolicyChange {
  readonly field: PolicyField;
  readonly value: PickValue | boolean;
}

interface PolicyChangeContext {
  readonly seq: number;
  readonly previous: PickValue | boolean;
}

export function usePolicySettings(): PolicySettingsState {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const announcer = useAnnouncer();
  const nowMs = useMinuteClock();
  const [refusals, setRefusals] = useState<PolicySettingsState['refusals']>({});
  // The newest change of each field: an older change's answer or refusal never overwrites it.
  const latest = useRef(new Map<PolicyField, number>());

  const policy = useQuery(bookingPolicyQueryOptions());
  // The hours are read for their zone (the notice's nearest start and the horizon's last day are on
  // the trainer's clock) and for „Kiedy pracuję"'s weekly total. Today is read on the browser's
  // clock first only to size the range; the range is days, and a day either way changes nothing.
  const readToday = wallReading(nowMs, bookingZone(null)).day;
  // Kept across the key change at the trainer's midnight, so the tab never drops back to its wait.
  const hours = useQuery({
    ...trainerAvailabilityQueryOptions(scheduleReadRange(readToday)),
    placeholderData: keepPreviousData,
  });
  const gate = useSkeletonGate(
    (policy.isPending && policy.errorUpdateCount === 0) ||
      (hours.isPending && hours.errorUpdateCount === 0),
  );

  const zone = bookingZone(hours.data?.trainerTimeZoneId);
  const context: SettingsContext = {
    nowMs,
    zone,
    today: wallReading(nowMs, zone).day,
    locale: i18n.language,
    weekMinutes:
      hours.data === undefined ? null : weeklyMinutes(weekPatternOf(hours.data.weekly ?? [])),
  };

  const key = bookingPolicyQueryOptions().queryKey;
  const setRefusal = (field: PolicyField, text: string | null) => {
    setRefusals((current) => {
      const next = { ...current };
      if (text === null) delete next[field];
      else next[field] = text;
      return next;
    });
  };

  // Every callback lives on the MUTATION, not on a `mutate` call: a call's own callbacks are
  // dropped for all but the newest call (query-core's `MutationObserver`), which lost the refusal
  // of the first of two quick changes. Each change is sequenced per field.
  const patch = useMutation({
    ...POLICY_WRITE,
    mutationFn: (change: PolicyChange) =>
      patchBookingPolicy(policyPatch(change.field, change.value)),
    onMutate: async (change): Promise<PolicyChangeContext | undefined> => {
      await queryClient.cancelQueries({ queryKey: key });
      const before = queryClient.getQueryData<BookingPolicy>(key);
      if (before === undefined) return undefined;
      const seq = (latest.current.get(change.field) ?? 0) + 1;
      latest.current.set(change.field, seq);
      setRefusal(change.field, null);
      queryClient.setQueryData(key, policyWithChange(before, change.field, change.value));
      return { seq, previous: policyFieldValue(before, change.field) };
    },
    onSuccess: (answered, change, context) => {
      if (context === undefined || latest.current.get(change.field) !== context.seq) return;
      // Only this field's reading is taken from the answer: another field may have a newer
      // change of its own still out.
      const current = queryClient.getQueryData<BookingPolicy>(key) ?? answered;
      queryClient.setQueryData(
        key,
        policyWithChange(current, change.field, policyFieldValue(answered, change.field)),
      );
      announcer.announce(t('web:booking.settings.saved'));
    },
    onError: (error, change, context) => {
      if (context === undefined || latest.current.get(change.field) !== context.seq) return;
      const current = queryClient.getQueryData<BookingPolicy>(key);
      if (current !== undefined) {
        queryClient.setQueryData(key, policyWithChange(current, change.field, context.previous));
      }
      const refused = scheduleRefusal(
        'savePolicy',
        error instanceof ApiError ? { status: error.status, code: error.code } : null,
      );
      setRefusal(change.field, `${t(refused.actKey)} ${t(refused.reasonKey, refused.params)}`);
    },
    onSettled: () => {
      // The server's reading once nothing of the tab's is still out, so a re-read cannot land on
      // a change that has not answered yet.
      if (policyWritesSettled(queryClient)) {
        void readBookingAgain(queryClient);
      }
    },
  });

  const change = (field: PolicyField, value: PickValue | boolean) => {
    if (queryClient.getQueryData<BookingPolicy>(key) === undefined) return;
    patch.mutate({ field, value });
  };

  const preview = useClientPreview(readState(policy), {
    nowMs,
    zone,
    zonePublished: readState({
      data: hours.data === undefined ? undefined : hours.data.trainerTimeZoneId != null,
      isError: hours.isError,
      errorUpdateCount: hours.errorUpdateCount,
    }),
  });

  const content: PolicySettingsContent = gate.waiting
    ? {
        kind: 'waiting',
        groups: policySettingsView(
          DEFAULT_POLICY,
          { ...context, weekMinutes: DEFAULT_WEEK_MINUTES },
          t,
        ),
      }
    : policy.data === undefined
      ? { kind: 'failed' }
      : {
          kind: 'ready',
          policy: policy.data,
          groups: policySettingsView(policy.data, context, t),
          context,
        };

  return {
    gate,
    content,
    pick: (field) =>
      policy.data === undefined ? null : policyPick(policy.data, field, context, t),
    change,
    refusals,
    announcement: announcer.message,
    retry: () => {
      if (policy.isError) void policy.refetch();
      if (hours.isError) void hours.refetch();
    },
    nowMs,
    zone,
    preview,
  };
}
